import { useState, useEffect, useRef, useCallback } from 'react';

const ELEMENT_SEARCH_DELAY_MS = 300;

/**
 * Find an element using multiple strategies (mirrors ShowUserContext.findShowableElement)
 */
function findElement(selector: string): HTMLElement | null {
  // Strategy 1: data-onboarding-id
  let el = document.querySelector<HTMLElement>(`[data-onboarding-id="${selector}"]`);
  if (el) return el;

  // Strategy 2: data-showable
  const normalized = selector.toLowerCase().replace(/\s+/g, '-');
  el = document.querySelector<HTMLElement>(`[data-showable="${selector}"]`);
  if (el) return el;
  el = document.querySelector<HTMLElement>(`[data-showable="${normalized}"]`);
  if (el) return el;

  // Strategy 3: CSS selector (e.g. "[data-sidebar='content']")
  try {
    el = document.querySelector<HTMLElement>(selector);
    if (el) return el;
  } catch {
    // Invalid selector, skip
  }

  // Strategy 4: ID
  el = document.getElementById(selector);
  if (el) return el;

  // Strategy 5: aria-label
  el = document.querySelector<HTMLElement>(`[aria-label*="${selector}" i]`);
  if (el) return el;

  return null;
}

export function useSpotlight(selector: string | null) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const selectorRef = useRef(selector);

  const updateRect = useCallback((el: HTMLElement) => {
    const domRect = el.getBoundingClientRect();
    setRect(domRect);
  }, []);

  useEffect(() => {
    selectorRef.current = selector;

    // Cleanup previous observer
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!selector) {
      setRect(null);
      setElement(null);
      return;
    }

    // Delay to allow React renders (tab switches, etc.)
    const searchTimeout = setTimeout(() => {
      // Bail if selector changed during delay
      if (selectorRef.current !== selector) return;

      const el = findElement(selector);
      if (!el) {
        setRect(null);
        setElement(null);
        return;
      }

      setElement(el);

      // Scroll into view
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Initial measurement (after scroll settles)
      setTimeout(() => {
        if (selectorRef.current !== selector) return;
        updateRect(el);
      }, 350);

      // Track rect via ResizeObserver + scroll/resize
      const handleUpdate = () => {
        if (selectorRef.current === selector) updateRect(el);
      };

      observerRef.current = new ResizeObserver(handleUpdate);
      observerRef.current.observe(el);
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);

      return () => {
        observerRef.current?.disconnect();
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }, ELEMENT_SEARCH_DELAY_MS);

    return () => {
      clearTimeout(searchTimeout);
      observerRef.current?.disconnect();
      window.removeEventListener('scroll', () => {}, true);
      window.removeEventListener('resize', () => {});
    };
  }, [selector, updateRect]);

  return { rect, element };
}
